import React, { type FunctionComponent } from 'react'
import { Text, spacing } from '@retailer-platform/shared-components'
import { css } from '@emotion/react'
import {
  MerchandisingFormSection,
  type Props as MerchandisingFormSectionProps,
} from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { HeroBannerPreview } from 'domains/merchandising/components/hero-banner-preview/HeroBannerPreview'
import { type ContentManagementStorefrontPlacement } from 'domains/merchandising/api'
import { useGetRetailerConfiguration } from 'domains/merchandising/api/getRetailerConfiguration.hooks'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useDashMessages } from 'intl/intl.hooks'

interface Props
  extends Partial<ContentManagementStorefrontPlacement>,
    Pick<MerchandisingFormSectionProps, 'titleSuffix'> {}

const mobilePreviewContainer = {
  maxWidth: 375,
}

const rowContainer = css({
  marginBottom: spacing.X24,
  width: '100%',
})

export const HeroBannerDetailsPreview: FunctionComponent<Props> = ({
  titleSuffix,
  button,
  images,
  imageAltText,
  descriptionText,
  destination,
  headingText,
  subheadingText,
  backgroundColor,
  type,
}) => {
  const retailerId = useRetailerId()
  const { retailerConfiguration } = useGetRetailerConfiguration({ retailerId })

  const messages = useDashMessages({
    largeImageLabel: 'merchandising.hero-banners.section.content.field.imageLarge.label',
    smallImageLabel: 'merchandising.hero-banners.section.content.field.imageSmall.label',
  })

  const previewProps = {
    heading: headingText,
    subheading: descriptionText,
    tag: subheadingText,
    background: {
      color: backgroundColor,
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    banner_image: {
      url: images.large,
      alt: imageAltText,
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    button_snacks_style: button.styleVariant.toLowerCase(),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    label_action: {
      action: {
        type: destination?.type,
        data: destination?.target,
      },
      label: button.label,
    },
    retailerConfiguration: retailerConfiguration,
    variant: type.toLowerCase(),
  }

  return (
    <MerchandisingFormSection
      titleId="merchandising.hero-banners.section.preview.title"
      titleSuffix={titleSuffix}
    >
      <div css={rowContainer}>
        <Text weight="semibold">{messages.largeImageLabel}</Text>
        <HeroBannerPreview {...previewProps} />
      </div>

      <div css={rowContainer}>
        <Text weight="semibold">{messages.smallImageLabel}</Text>
        <div css={mobilePreviewContainer}>
          <HeroBannerPreview
            {...previewProps}
            mobile
            banner_image={{ url: images.large, alt: imageAltText }}
          />
        </div>
      </div>
    </MerchandisingFormSection>
  )
}
